import { keyedEnum } from '../catalogs/enum.js';
import { addMoney, minMoney, sumBy } from '../money/money.js';
import { accruedInterestInProgress, NO_ACCRUAL } from './accrual.js';
import { installmentBalance, isUntouched, regularOutstanding, } from './installment.js';
/**
 * Qué hacer con el dinero que excede lo exigible hoy:
 * - `advance_installments`: pagar por adelantado las siguientes cuotas
 *   (adelanto de cuotas). El plan no cambia.
 * - `reduce_installment`: abono a capital conservando el plazo; las cuotas
 *   restantes bajan.
 * - `reduce_term`: abono a capital conservando la cuota; el préstamo termina
 *   antes.
 *
 * El prepago no tiene penalización. Al abonar a capital se cobra primero el
 * interés devengado del período en curso (días ya transcurridos sobre el
 * saldo anterior) y desde ese día el capital prepagado deja de devengar.
 */
export const EXCESS_STRATEGIES = ['advance_installments', 'reduce_installment', 'reduce_term'];
/** Catálogo con clave de estrategias de excedente: `EXCESS_STRATEGY.reduce_term`. */
export const EXCESS_STRATEGY = keyedEnum(EXCESS_STRATEGIES);
/** Etiquetas en español de cada estrategia de excedente. */
export const EXCESS_STRATEGY_LABELS = {
    advance_installments: 'Adelantar cuotas',
    reduce_installment: 'Abono a capital: reducir cuota',
    reduce_term: 'Abono a capital: reducir plazo',
};
/** Orden de aplicación dentro de una cuota: primero cargos, al final capital. */
const COMPONENT_ORDER = ['lateFee', 'lateInterest', 'lateTax', 'tax', 'interest', 'principal'];
/** Línea de aplicación en cero que conserva la mora cristalizada de la cuota. */
function emptyLine(inst) {
    return {
        installmentId: inst.id,
        periodNumber: inst.periodNumber,
        lateFee: 0,
        lateInterest: 0,
        lateTax: 0,
        tax: 0,
        interest: 0,
        principal: 0,
        lateInterestAccrued: inst.lateInterestAccrued,
        lateAccruedThrough: inst.lateAccruedThrough,
        lateFeeCharged: inst.lateFeeCharged,
    };
}
/**
 * Distribuye un pago entre las cuotas, de la más antigua a la más reciente,
 * según la prelación: gasto de cobranza → interés moratorio → IVA moratorio →
 * IVA → interés → capital. Es pura: quien la llama persiste el resultado.
 */
export function allocatePayment(input) {
    const { paymentDate, policy } = input;
    const ordered = [...input.installments].sort((a, b) => a.periodNumber - b.periodNumber);
    const lines = new Map();
    let remaining = input.amount;
    const apply = (inst, owed) => {
        const line = lines.get(inst.id) ?? emptyLine(inst);
        for (const component of COMPONENT_ORDER) {
            const take = Math.min(remaining, owed[component]);
            line[component] = (line[component] + take);
            remaining -= take;
        }
        lines.set(inst.id, line);
    };
    // 1. Todo lo vencido hasta la fecha del pago, incluida la mora.
    for (const inst of ordered.filter((i) => i.dueDate <= paymentDate)) {
        const balance = installmentBalance(inst, paymentDate, policy);
        if (balance.total === 0)
            continue;
        const line = emptyLine(inst);
        line.lateInterestAccrued = balance.lateInterestAccruedAsOf;
        line.lateFeeCharged = balance.lateFeeChargedAsOf;
        if (balance.daysLate > policy.graceDays)
            line.lateAccruedThrough = paymentDate;
        lines.set(inst.id, line);
        if (remaining > 0)
            apply(inst, balance);
    }
    // 2. Excedente sobre lo exigible.
    const future = ordered.filter((i) => i.dueDate > paymentDate);
    const regularOwed = (inst) => ({
        ...regularOutstanding(inst),
        lateFee: 0,
        lateInterest: 0,
        lateTax: 0,
    });
    let prepaidPrincipal = 0;
    let accruedCollected = null;
    if (input.excessStrategy === EXCESS_STRATEGY.advance_installments) {
        for (const inst of future)
            if (remaining > 0)
                apply(inst, regularOwed(inst));
    }
    else {
        // Completar las cuotas ya abonadas y luego prepagar capital sobre el resto.
        for (const inst of future.filter((i) => !isUntouched(i)))
            if (remaining > 0)
                apply(inst, regularOwed(inst));
        const prepayable = sumBy(future.filter(isUntouched), (i) => i.principalDue);
        const accrual = prepayable > 0 && remaining > 0 ? accruedInterestInProgress(ordered, paymentDate, input.interestStartDate) : null;
        const accruedOwed = accrual ? accrual.interest + accrual.tax : 0;
        const current = accrual ? ordered.find((i) => i.id === accrual.installmentId) : undefined;
        if (accrual && current && remaining <= accruedOwed) {
            // No alcanza para abonar a capital: se adelanta el pago de la cuota en curso.
            apply(current, regularOwed(current));
        }
        else {
            accruedCollected = accrual;
            remaining -= accruedOwed;
            prepaidPrincipal = minMoney(remaining, prepayable);
            remaining -= prepaidPrincipal;
        }
    }
    const allLines = [...lines.values()];
    const sum = (component) => sumBy(allLines, (l) => l[component]);
    const accrued = accruedCollected ?? NO_ACCRUAL;
    return {
        lines: allLines,
        prepaidPrincipal: prepaidPrincipal,
        accruedCollected,
        unapplied: remaining,
        totals: {
            lateFee: sum('lateFee'),
            lateInterest: sum('lateInterest'),
            lateTax: sum('lateTax'),
            tax: addMoney(sum('tax'), accrued.tax),
            interest: addMoney(sum('interest'), accrued.interest),
            principal: addMoney(sum('principal'), prepaidPrincipal),
        },
    };
}
/**
 * Fotografía de lo que debe un cliente a una fecha: base de cotizaciones y
 * topes. La liquidación incluye el interés devengado del período en curso.
 *
 * @param interestStartDate Fecha desde la que corre el interés de la primera cuota (desembolso).
 */
export function loanPosition(installments, asOf, policy, interestStartDate) {
    const ordered = [...installments].sort((a, b) => a.periodNumber - b.periodNumber);
    const due = ordered.filter((i) => i.dueDate <= asOf).map((i) => installmentBalance(i, asOf, policy));
    const future = ordered.filter((i) => i.dueDate > asOf);
    const regularTotal = (i) => {
        const r = regularOutstanding(i);
        return r.principal + r.interest + r.tax;
    };
    const dueToday = sumBy(due, (b) => b.total);
    const touchedFutureOwed = sumBy(future.filter((i) => !isUntouched(i)), regularTotal);
    const untouchedPrincipal = sumBy(future.filter(isUntouched), (i) => i.principalDue);
    const next = future.find((i) => regularTotal(i) > 0);
    const accrual = untouchedPrincipal > 0 ? accruedInterestInProgress(ordered, asOf, interestStartDate) : null;
    const accruedInterest = (accrual ? accrual.interest + accrual.tax : 0);
    const payoffAmount = (dueToday + touchedFutureOwed + untouchedPrincipal + accruedInterest);
    return {
        dueToday,
        lateCharges: sumBy(due, (b) => b.lateInterest + b.lateTax + b.lateFee),
        outstandingPrincipal: sumBy(ordered, (i) => regularOutstanding(i).principal),
        accruedInterest,
        payoffAmount,
        maxPayment: {
            advance_installments: (dueToday + sumBy(future, regularTotal)),
            reduce_installment: payoffAmount,
            reduce_term: payoffAmount,
        },
        nextDueDate: next?.dueDate ?? null,
        nextInstallmentAmount: next ? regularTotal(next) : 0,
        daysPastDue: Math.max(0, ...due.map((b) => b.daysLate)),
    };
}
//# sourceMappingURL=allocation.js.map